// WIDGET — Assessment Review Velocity: completed review actions per application across three
// rolling windows, answering "is our review team keeping up?".
// Self-fetching: calls its own dedicated endpoint
// (GET /dashboard/grc/system-owner/review-velocity).
import { useCallback, useEffect, useState } from "react";
import { ArrowRight, AlertTriangle, Gauge, Info } from "lucide-react";
import { Card, SectionTitle } from "../../GRCManagerDashboard/grcDashboardWidgets";
import { DataTable, type Column } from "../../../components/DataTable";
import { Sheet, SheetContent } from "../../../components/ui/sheet";
import { Tip } from "../../../components/ui/tooltip";
import { EmptyState } from "../../../components/EmptyState";
import { SkeletonList } from "../../../components/Skeleton";
import { CATEGORICAL_HEX, NOT_IDENTIFIED_HEX } from "../data/applicationKriData";
import {
  fetchReviewVelocity,
  type ReviewVelocityAppRow,
  type ReviewVelocityResponse,
} from "../../../../api/frameworksApi";

/* Same fixed height every other KRI card on this page uses, so the cards read
   as one system regardless of how many rows they happen to hold. */
const KRI_CARD = "flex h-[520px] flex-col";

const TOP_N = 5;

/* The three windows are cumulative (7d <= 30d <= 90d), so they are shown as three
   independent numbers — never stacked, which would triple-count the same review. */
const WINDOWS = [
  { key: "last7Days", label: "7 Days", hex: CATEGORICAL_HEX.series1 },
  { key: "last30Days", label: "30 Days", hex: CATEGORICAL_HEX.series3 },
  { key: "last90Days", label: "90 Days", hex: CATEGORICAL_HEX.series4 },
] as const;

/** lastReviewedAt is independent of the three windows — an app with all-zero
 *  counters can still have an older timestamp, which is more informative than
 *  a flat "no activity". null means it has never been reviewed. */
function reviewedAtLabel(iso: string | null): string {
  if (!iso) return "never reviewed";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "never reviewed";
  return `last reviewed ${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
}

/** An app with no SSP is a legitimate placeholder row rather than an error;
 *  with no SSP there is nothing that could have been reviewed. */
function rowSubtitle(app: ReviewVelocityAppRow): string {
  if (app.sspCount === 0) return "No SSP published yet";
  return `${app.sspCount} SSP${app.sspCount === 1 ? "" : "s"} (${reviewedAtLabel(app.counts.lastReviewedAt)})`;
}
function isDormant(app: ReviewVelocityAppRow): boolean {
  return app.sspCount === 0 || app.counts.last90Days === 0;
}

/* Fixed opacity for consistent color shading across all values. */
const CELL_ALPHA = 0.2;

/* Heatmap tile used as a table cell: consistent color with border outline,
   like the Controls with Highest Failure Rate widget. All values (including
   zero) display with the same colored background and solid border. */
function HeatCell({ value, hex, max, dormant }: { value: number; hex: string; max: number; dormant: boolean }) {
  const bg = dormant ? "transparent" : `${hex}${Math.round(CELL_ALPHA * 255).toString(16).padStart(2, "0")}`;
  const border = dormant ? `1px solid ${NOT_IDENTIFIED_HEX}55` : `1.5px solid ${hex}`;
  const textColor = dormant ? NOT_IDENTIFIED_HEX : hex;

  return (
    <div
      className="mx-auto flex h-9 w-14 items-center justify-center rounded-md text-xs font-bold tabular-nums transition-colors"
      style={{
        background: bg,
        color: textColor,
        border: border,
      }}
    >
      {value}
    </div>
  );
}

/* Portfolio pulse — the three server-computed totals. Read straight from
   `portfolio`; deliberately not re-summed from applications[]. */
function PortfolioPulse({ portfolio }: { portfolio: ReviewVelocityResponse["portfolio"] }) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-2">
      {WINDOWS.map((w) => (
        <div key={w.key} className="flex items-baseline gap-1.5">
          <span className="text-xl font-bold tabular-nums" style={{ color: w.hex }}>
            {portfolio[w.key]}
          </span>
          <span className="text-xs font-medium text-foreground/70">
            control{portfolio[w.key] === 1 ? "" : "s"} reviewed in {w.label.toLowerCase()}
          </span>
        </div>
      ))}
    </div>
  );
}

/* A function of the visible rows, not a static const — each window column's
   heat scale is relative to the max within the rows actually on screen, so
   the main card (top 5) and the "View More" drawer (all apps) each get their
   own correctly-scaled intensity instead of sharing one skewed scale. */
function buildColumns(rows: ReviewVelocityAppRow[]): Column<ReviewVelocityAppRow>[] {
  const maxByWindow = Object.fromEntries(
    WINDOWS.map((w) => [w.key, Math.max(1, ...rows.map((a) => a.counts[w.key]))]),
  ) as Record<(typeof WINDOWS)[number]["key"], number>;

  return [
    {
      key: "applicationName",
      header: "Application",
      align: "left",
      headerAlign: "left",
      render: (a) => (
        <div className="min-w-0">
          <p className={`text-sm font-semibold ${isDormant(a) ? "text-foreground/50" : "text-foreground"}`}>
            {a.applicationName}
          </p>
          <p className="text-xs text-muted-foreground">{rowSubtitle(a)}</p>
        </div>
      ),
    },
    ...WINDOWS.map(
      (w): Column<ReviewVelocityAppRow> => ({
        key: w.key,
        // Two-line header — "Controls Reviewed" names what these numbers count
        // (each terminal review action = one control statement), spelled out
        // here so the column is self-explanatory without hovering the (i).
        header: (
          <div className="leading-tight">
            <div>{w.label}</div>
            <div className="text-[9px] font-normal normal-case text-foreground/60">Controls Reviewed</div>
          </div>
        ),
        render: (a) => <HeatCell value={a.counts[w.key]} hex={w.hex} max={maxByWindow[w.key]} dormant={isDormant(a)} />,
      }),
    ),
  ];
}

/* ══ Assessment Review Velocity — main export ═══════════════════════════ */
export function ReviewVelocityWidget() {
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [data, setData] = useState<ReviewVelocityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchReviewVelocity()
      .then((res) => setData(res?.data ?? null))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load review velocity"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Server-sorted most-active-first, so the top slice is already the right one.
  const apps = data?.applications ?? [];
  const topApps = apps.slice(0, TOP_N);
  const reviewedApps = apps.filter((a) => !!a.counts.lastReviewedAt);
  const portfolio = data?.portfolio ?? { last7Days: 0, last30Days: 0, last90Days: 0 };

  const showSkeleton = loading && !data;
  const showError = !!error && !data;

  return (
    <Card className={KRI_CARD}>
      <div className="mb-1 flex items-start justify-between gap-2">
        <SectionTitle
          title="Assessment Review Velocity"
          subtitle="Reviews completed this week, month, and quarter."
        />
        <div className="flex shrink-0 items-center gap-2">
          <Tip
            label={
              <span className="block max-w-[280px]">
                Counts only completed review actions — a reviewer accepting or rejecting a statement. Follow-up
                requests, drafts and pending reviews are not counted, so this measures work closed rather than work
                touched. Windows are rolling from right now, not calendar months, and they overlap: a review from
                yesterday appears in all three.
              </span>
            }
          >
            <Info className="size-4 shrink-0 text-muted-foreground" />
          </Tip>
          {apps.length > 0 && (
            <button
              onClick={() => setViewAllOpen(true)}
              className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-indigo-500/50 px-2.5 py-1 text-xs font-semibold text-foreground transition-colors hover:border-indigo-500 hover:bg-indigo-500/10"
            >
              View More <ArrowRight className="size-3.5" />
            </button>
          )}
        </div>
      </div>

      {!showSkeleton && !showError && <PortfolioPulse portfolio={portfolio} />}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {showSkeleton ? (
          <SkeletonList rows={5} />
        ) : showError ? (
          <EmptyState
            icon={AlertTriangle}
            title="Couldn't load this widget"
            description={error!}
            action={
              <button
                onClick={load}
                className="rounded-lg border border-indigo-500/50 px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-indigo-500 hover:bg-indigo-500/10"
              >
                Retry
              </button>
            }
          />
        ) : apps.length === 0 ? (
          <EmptyState icon={Gauge} title="No applications yet" description="No applications are visible to you, so there is no review activity to report." />
        ) : (
          <DataTable
            columns={buildColumns(topApps)}
            data={topApps}
            rowKey={(a) => a.applicationId}
            emptyText="No Data Available"
            hidePageButtons
            compact
          />
        )}
      </div>

      <Sheet open={viewAllOpen} onOpenChange={setViewAllOpen}>
        <SheetContent side="right" className="flex h-full max-h-screen w-[96vw] flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[1000px]">
          <div className="shrink-0 border-b border-border p-6">
            <h2 className="text-xl font-bold text-foreground">Assessment Review Velocity</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              All {reviewedApps.length} application{reviewedApps.length === 1 ? "" : "s"}, most active first.
            </p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-6">
            <PortfolioPulse portfolio={portfolio} />
            <DataTable columns={buildColumns(reviewedApps)} data={reviewedApps} rowKey={(a) => a.applicationId} emptyText="No Data Available" />
          </div>
        </SheetContent>
      </Sheet>
    </Card>
  );
}
